/* eslint-disable @typescript-eslint/no-explicit-any */
// src/features/auth/forgotPasswordSlice.ts
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { apiService } from "../../services/apiService"; // Adjust import based on location

interface ForgotPasswordState {
  token: string | null;
  loading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: ForgotPasswordState = {
  token: null,
  loading: false,
  error: null,
  successMessage: null,
};

export const forgotPassword = createAsyncThunk<
  any, // Return type for fulfilled case
  string, // Input argument (email)
  { rejectValue: { message: string; status: number } } // Type for rejectWithValue
>("auth/request-password-reset", async (email, { rejectWithValue }) => {
  try {
    const response = await apiService.forgotPassword(email);
    return response.data;
  } catch (error: any) {
    toast.error(error?.response?.data?.message || "Something went wrong", {
      className: "md:!bg-[#FD7272]/20 !bg-[#FD7272]/100 dark:!text-white dark:text-[#191919] !shadow-none",
      duration: 2000,
    });

    const errMessage = error.response?.data?.message || "Something went wrong";
    const status = error.response?.status || 500;

    // Use the typed rejectWithValue
    return rejectWithValue({ message: errMessage, status });
  }
});

// Thunk for change password
// Thunk for change password
export const changePassword = createAsyncThunk<
  any, // Return type for fulfilled case
  { token: string; newPassword: string }, // Input argument type
  { rejectValue: { message: string; status: number } } // Type for rejectWithValue
>("auth/reset-password", async ({ token, newPassword }, { rejectWithValue }) => {
  try {
    const response = await apiService.changePassword(token, newPassword);
    return response.data; // Return the response data if successful
  } catch (error: any) {
    const errMessage = error.response?.data?.message || "Something went wrong";
    const status = error.response?.status || 500;

    return rejectWithValue({ message: errMessage, status });
  }
});

const forgotPasswordSlice = createSlice({
  name: "forgotPassword",
  initialState,
  reducers: {
    resetForgotPasswordState: (state) => {
      state.error = null;
      state.successMessage = null;
    },
    setError(state, action) {
      state.error = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(forgotPassword.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
      .addCase(forgotPassword.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Password reset link sent successfully!";
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.loading = false;

        if (action.payload) {
          state.error = action.payload.message; // Accessing message property
        } else {
          state.error = "An unexpected error occurred"; // Fallback error message
        }
      });
  },
});

export const { resetForgotPasswordState, setError } = forgotPasswordSlice.actions;
export default forgotPasswordSlice.reducer;
